// @ts-nocheck
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  Validators,
} from '@angular/forms';
import { isNull, uniqBy } from 'lodash';
import { Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Component({
  selector: 'lib-input-years-months',
  templateUrl: './input-years-months.component.html',
  styleUrls: ['./input-years-months.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputYearsMonthsComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputYearsMonthsComponent),
      multi: true,
    },
  ],
  exportAs: 'libInputYearMonths',
})
export class InputYearsMonthsComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  // value store
  @ViewChild('input', { static: true }) input: ElementRef<HTMLInputElement>;

  @Input() theme: 'primary' | 'warn' | 'stroked' | 'stroked-primary' | 'stroked-warn';

  @Input() showMonths = true;

  get class() {
    return `lib-input-years-months${this.theme ? `-${this.theme}` : ''}`;
  }

  // tslint:disable-next-line: variable-name
  private _min = 0; // in months (0 years default)

  @Input()
  set min(value: number) {
    this._min = value;
    this.setOptions();
  }

  // tslint:disable-next-line: variable-name
  private _max = 1200; // in months (100 years default)

  @Input()
  set max(value: number) {
    this._max = value;
    this.setOptions();
  }

  yearsOptions = [];

  monthsOptions = [];

  @Input()
  set value(value: any) {
    this.writeValue(value);
  }

  get value(): any {
    return this.data;
  }

  @Input()
  set disabled(value: string | boolean) {
    this.setDisabledState(value === '' || value === 'true' || value === true);
  }

  @Output() update: EventEmitter<any> = new EventEmitter();

  data: any;

  group: FormGroup = this.formBuilder.group({
    years: [{ value: null, disabled: false }, Validators.compose([Validators.min(0)])],
    months: [{ value: null, disabled: false }, Validators.compose([Validators.min(0), Validators.max(11)])],
  });

  groupSubscription: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private renderer: Renderer2,
  ) { }

  ngOnInit(): void {
    this.setOptions();

    this.groupSubscription = this.group.valueChanges
      .pipe(
        map((values) => (!isNull(values.years) || !isNull(values.months)
          ? (parseInt(values.years || 0, 0) * 12) + parseInt(values.months || 0, 0)
          : null)),
      )
      .subscribe(this.writeValue.bind(this));
  }

  ngOnDestroy(): void {
    if (this.groupSubscription) {
      this.groupSubscription.unsubscribe();
      this.groupSubscription = void 0;
    }
  }

  writeValue(obj: any): void {
    if (this.data === obj) { return; }

    this.data = obj;

    const maxYears = Math.floor(this._max / 12);
    // console.log(`max ${maxYears}`);

    let months = 0;
    let enableMonths = true;
    let years = obj ? Math.floor(obj / 12) : obj;

    if (years >= maxYears) {
      years = maxYears;
      months = 0;
      enableMonths = false;
    } else {
      months = obj - (years * 12);
    }

    // console.log(`${years} years ${months} months`);
    // console.log(`enable months: ${enableMonths}`);

    this.group.setValue({
      years,
      months,
    }, { emitEvent: false });

    const monthsControl = this.group.get('months');
    if (enableMonths) {
      monthsControl.enable();
    } else {
      monthsControl.disable();
    }

    const inputElement: HTMLInputElement = this.input.nativeElement;
    inputElement.value = this.data;

    this.propagateChange(this.data);

    this.update.emit(this.data);
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void { }

  setDisabledState(isDisabled: boolean) {
    this.renderer.setProperty(this.input?.nativeElement, 'disabled', isDisabled);
  }

  onChange(event: Event) {
    const inputElement: HTMLInputElement = event?.target as HTMLInputElement;
    this.data = inputElement?.value;
    // this.writeValue(inputElement?.value);
    this.propagateChange(this.data);
    this.update.emit(this.data);
  }

  validate(control: AbstractControl): { [key: string]: any } | null {
    return null;
  }

  private propagateChange = (_: any) => { };

  private setOptions(): void {
    const range = this._max - this._min;

    this.yearsOptions = uniqBy(new Array(range)
      .fill(0)
      .map((v, i) => {
        const month = i + this._min;
        const year = Math.ceil(month / 12);
        return {
          label: `${year} yrs`,
          value: year,
        };
      }), 'value');

    this.monthsOptions = new Array(this._min > 0 && this._min < 12 ? this._min : 12)
      .fill(0)
      .map((v, i) => {
        const month = i;
        return {
          label: `${month} m`,
          value: month,
        };
      });
  }
}
